import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Loader, Modal, Button, Checkbox,
} from 'ui-library';
import {
    Route, Routes, useNavigate,
} from 'react-router-dom';
import AutoLogout from 'domain/Auth/AutoLogout';
import { Router as RootRouter } from 'pages';
import ScrollToTop from 'components/ScrollToTop';
import ErrorBoundary from 'components/ErrorBoundary';
import { ApolloClientProvider } from 'services/ApolloClient';
import { useActiveSystemMessage } from 'prodivers/ActiveSystemMessageProvider';
import SystemMessagePreview from 'components/SystemMessagePreview';
import Icon, { ICON_TYPE_CLOSE } from 'ui-library/components/Icon';
import { getCurrentEnv } from './utils/support';
import { CheckExpiredSessionStorageTokens } from './session-storage-tokens/session-storage-tokens';
import { getAutoLogoutConfig } from './configs/auto-logout-config';

import './App.css';

const App = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    CheckExpiredSessionStorageTokens();

    const {
        hasActiveSystemMessage,
        activeSystemMessageData,
        isMarkedAsRead,
        markAsRead,
        isLoading,
        error,
        isMutationLoading,
        isPreviewActive,
        setPreviewMessageStatus,
    } = useActiveSystemMessage();

    const [isCheckedAsRead, setIsCheckedAsRead] = useState(isMarkedAsRead);

    useEffect(() => {
        setIsCheckedAsRead(isMarkedAsRead);
    }, [isMarkedAsRead]);

    useEffect(() => {
        const systemMessageClosed = JSON.parse(sessionStorage.getItem('systemMessageClosed') || 'false');

        if (hasActiveSystemMessage && !isLoading && !error && !systemMessageClosed && !isMarkedAsRead) {
            setPreviewMessageStatus?.(true);
        }
    }, [hasActiveSystemMessage, isLoading, error, isMarkedAsRead, setPreviewMessageStatus]);

    const handleCloseSystemMessage = () => {
        setPreviewMessageStatus?.(false);
        if (!isMarkedAsRead) {
            sessionStorage.setItem('systemMessageClosed', 'true');
        }
    };

    const handleMarkAsRead = async () => {
        try {
            await markAsRead?.(activeSystemMessageData?.id);
            setIsCheckedAsRead(true);
        } catch (markError) {
            setIsCheckedAsRead(false);
        }
    };

    return (
        <Suspense fallback={<Loader />}>
            <ApolloClientProvider>
                <ErrorBoundary navigate={navigate}>
                    <AutoLogout config={getAutoLogoutConfig()} />
                    <div className={`env-bar ${getCurrentEnv()} `}>
                        <strong>{getCurrentEnv().toUpperCase()}</strong>
                    </div>

                    <ScrollToTop />
                    <Routes>
                        <Route path="/*" element={<RootRouter />} />
                    </Routes>

                    {isPreviewActive && (
                        <Modal
                            visible
                            width={800}
                            height="90%"
                            header={<span/>}
                            footer={<span/>}
                            borderRadius
                            closable
                            closeIcon={
                                <Icon type={ICON_TYPE_CLOSE} size={16} />
                            }
                            onCancel={handleCloseSystemMessage}
                            className="system-message-modal"
                        >
                            <SystemMessagePreview
                                title={activeSystemMessageData?.title}
                                body={activeSystemMessageData?.body}
                                link={activeSystemMessageData?.url}
                                document={activeSystemMessageData?.document}
                            />
                            <div className="system-message-field-seperator" />
                            <div className="system-message-fields--actions flex-end">
                                <Checkbox
                                    id="mark-message-read-checkbox"
                                    onChange={handleMarkAsRead}
                                    checked={isCheckedAsRead}
                                    disabled={
                                        isMarkedAsRead || isMutationLoading || isCheckedAsRead
                                    }
                                    label={t(
                                        'SystemMessage.PreviewMessageModal.DontShowAgain',
                                    )}
                                    onlyExternalState
                                />

                                <Button
                                    type="primary"
                                    onClick={handleCloseSystemMessage}
                                >
                                    {t('clientDashboard.close')}
                                </Button>
                            </div>
                        </Modal>
                    )}
                </ErrorBoundary>
            </ApolloClientProvider>
        </Suspense>
    );
};

export default App;
